import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { CollapseContainer, CollapseItem, Icon, Text, useTranslation, useUi } from '@weareconceptstudio/core';

//* Styles
import FilterStyle from './style';

//* Helpers
import { getRecipesTags } from '@/libs/get-data';

//* Components
import CustomCheckboxGroup from './CustomCheckboxGroup';
import Button from '@/components/Button';

const Filter = ({ selected, setSelected }) => {
	const pathname = usePathname();
	const router = useRouter();
	const searchParams = useSearchParams();

	const { selectedLang } = useTranslation();
	const { winWidth } = useUi();

	const [recipesData, setRecipesData] = useState(null);

	const [filterActiveMobileMenu, setFilterActiveMobileMenu] = useState(false);

	//! Ref
	const styleRef = useRef();

	//! Checks
	const isMobile = useMemo(() => {
		return winWidth < 768;
	}, [winWidth]);

	useEffect(() => {
		if (filterActiveMobileMenu && winWidth >= 768) {
			setFilterActiveMobileMenu(false);
		}
	}, [winWidth, filterActiveMobileMenu]);

	useEffect(() => {
		setFilterActiveMobileMenu(false);
	}, [pathname, selectedLang]);

	//! Overflow body
	useEffect(() => {
		filterActiveMobileMenu ? document?.body?.classList?.add('filter-hidden') : document?.body?.classList?.remove('filter-hidden');
	}, [filterActiveMobileMenu, pathname, selectedLang]);

	//! Fetch Data
	useEffect(() => {
		const fetchData = async () => {
			const data = await getRecipesTags(selectedLang);
			setRecipesData(data);
		};

		fetchData();
	}, [selectedLang]);

	//! Handle Select
	const handleSelect = (key, value) => {
		setSelected((prev) => {
			let keyData = prev[key] || [];

			if (keyData.includes(value)) {
				keyData = keyData.filter((item) => item !== value);
			} else {
				keyData.push(value);
			}

			return {
				...prev,
				[key]: keyData,
			};
		});
	};

	//! Custom query string
	const createQueryString = (selectedFilters) => {
		const params = new URLSearchParams();

		Object.entries(selectedFilters).forEach(([key, values]) => {
			if (Array.isArray(values) && values.length > 0) {
				params.set(key, values.join(','));
			}
		});

		return params.toString();
	};

	//! On filter Change
	const onFilterChange = () => {
		const queryString = createQueryString(selected);
		const newPath = `${pathname}?${queryString}`;
		const oldPath = `${pathname + '?' + searchParams.toString()}`;

		if (newPath !== oldPath) {
			router.push(`${pathname}?${queryString}`);
		}
		closeCollapse();
	};

	//! Did Mount
	useEffect(() => {
		window.addEventListener('click', handleWindowClick);

		return () => {
			window.removeEventListener('click', handleWindowClick);
		};
	}, []);

	//! Window Click Listener for close
	const handleWindowClick = useCallback((e) => {
		if (!styleRef.current.contains(e.target)) {
			closeCollapse();
		}
	}, []);

	function closeCollapse() {
		if (styleRef.current) {
			const item = styleRef.current.querySelector('#collapse-item');
			if (item) {
				item.setAttribute('status', 'close');
			}
		}
	}

	//! Is filter active
	const isFilterActive = useMemo(() => {
		return Object.values(selected).some((arr) => Array.isArray(arr) && arr.length > 0);
	}, [selected]);

	//! Has Search Params
	const hasSearchParams = useMemo(() => {
		return searchParams.size === 0 ? true : false;
	}, [searchParams]);

	//! Filter Store
	const filterStore = useMemo(() => {
		return (
			<div className={`filter-content-wrapper ${filterActiveMobileMenu ? 'active' : ''}`}>
				<div className='filter-content-inner-wrapper'>
					<div className='filter-content-flex-wrapper'>
						<div className='left-wrapper'>
							{isMobile ? (
								<div className='filter-mobile-title-block'>
									<Text
										className={'green-1000 h3 font-larken-regular filter-by-mobile-text'}
										text={'filterBy'}
									/>

									{/* <div
										className='filter-mobile-close-block'
										onClick={() => setFilterActiveMobileMenu(false)}
									> */}
									<Icon
										name={'filter-close'}
										className='p1 filter-mobile-close cursor-pointer'
										onClick={() => setFilterActiveMobileMenu(false)}
									/>
									{/* </div> */}
								</div>
							) : null}

							<div className='top-block'>
								<CustomCheckboxGroup
									title={'byMeal'}
									options={recipesData?.meal}
									selected={selected}
									filterKey={'type_meal_id'}
									onSelect={handleSelect}
								/>

								<CustomCheckboxGroup
									title={'byDietaryNeeds'}
									options={recipesData?.dietary_needs}
									selected={selected}
									filterKey={'type_dietary_needs_id'}
									onSelect={handleSelect}
								/>

								<CustomCheckboxGroup
									title={'byIngredients'}
									options={recipesData?.ingredients}
									selected={selected}
									filterKey={'type_ingredients_id'}
									onSelect={handleSelect}
								/>

								<CustomCheckboxGroup
									title={'difficulty'}
									options={recipesData?.difficulty}
									selected={selected}
									filterKey={'type_difficulty_id'}
									onSelect={handleSelect}
								/>

								<CustomCheckboxGroup
									title={'byProducts'}
									options={recipesData?.product}
									selected={selected}
									filterKey={'type_product_id'}
									onSelect={handleSelect}
								/>
							</div>

							<div className='bottom-block'>
								<Button
									btnType={'yellow-type'}
									text={'apply'}
									disabled={!isFilterActive}
									onClick={() => {
										setFilterActiveMobileMenu(false), onFilterChange();
									}}
								/>

								<Button
									text={'clearAll'}
									disabled={hasSearchParams}
									onClick={() => {
										setSelected({});
										closeCollapse();
										router.push(pathname);
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}, [recipesData, isMobile, filterActiveMobileMenu, selected, isFilterActive, hasSearchParams, pathname, handleSelect, onFilterChange, router, setSelected]);

	return (
		<FilterStyle
			ref={styleRef}
			className='filter-wrapper'
		>
			<div className='filter-inner-wrapper'>
				{isMobile ? (
					<>
						<div
							className='filter-title-block'
							onClick={() => setFilterActiveMobileMenu(true)}
						>
							<Text
								className={'green-1000 p4 font-fss-regular filter-by-text'}
								text={'filterBy'}
							/>

							<Icon
								name={'filter-menu'}
								className='p1'
							/>
						</div>

						{filterStore}
					</>
				) : (
					<CollapseContainer>
						<CollapseItem
							title={
								<div className='filter-title-block'>
									<Text
										className={'green-1000 p1 font-fss-regular filter-by-text'}
										text={'filterBy'}
									/>

									<Icon
										name={'dropdown-arr'}
										className='p2 filter-arrow'
									/>
								</div>
							}
							description={filterStore}
						></CollapseItem>
					</CollapseContainer>
				)}
			</div>
		</FilterStyle>
	);
};

export default Filter;
